import { httpsCallable } from 'firebase/functions'
import { create } from 'zustand'
import { listCalendars, listEvents, type TokenGetter } from '@/data/calendarApi'
import { functions } from '@/lib/firebase'
import { requestAuthCode } from '@/lib/google'
import { idbDelete, idbGet, idbSet } from '@/lib/idb'
import { addDays, dateTimeFrom } from '@/logic/dates'
import { isShown, normalizeCalendar, normalizeEvent, type CalEvent, type GCalendar } from '@/logic/events'
import type { DateStr, Settings } from '@/types'

// Google Calendar on the device: the connection, a short-lived access token
// held in memory only, the calendar list, and the events for the window the
// views are looking at. Cuplzap only reads; Google stays the home of events.

export type Connection = 'unknown' | 'checking' | 'connected' | 'disconnected' | 'revoked'

interface CalendarState {
  connection: Connection
  busy: boolean
  error: string | null
  calendars: GCalendar[]
  events: CalEvent[]
  window: { from: DateStr; to: DateStr } | null
  /** When the events were last fetched from Google, or null when never. */
  asOf: number | null
  /** True while the events shown came from the device's cache. */
  fromCache: boolean
}

export const useCalendar = create<CalendarState>()(() => ({
  connection: 'unknown',
  busy: false,
  error: null,
  calendars: [],
  events: [],
  window: null,
  asOf: null,
  fromCache: false,
}))

let token: { accessToken: string; expiresAt: number } | null = null

const callConnect = httpsCallable<{ code: string; redirectUri: string }, { connected: boolean; needsConsent?: boolean; accessToken?: string; expiresAt?: number }>(functions, 'connectCalendar')
const callToken = httpsCallable<void, { accessToken: string; expiresAt: number }>(functions, 'getCalendarAccessToken')
const callDisconnect = httpsCallable<void, { ok: boolean }>(functions, 'disconnectCalendar')

function codeOf(err: unknown): string {
  return ((err as { code?: string }).code ?? '').replace('functions/', '')
}

function messageOf(err: unknown): string {
  return (err as { message?: string }).message ?? 'Something went wrong.'
}

/** The access token, refreshed through the function when missing, expired or forced. */
export const getToken: TokenGetter = async (force = false) => {
  if (!force && token && token.expiresAt > Date.now() + 30_000) return token.accessToken
  try {
    const r = await callToken()
    token = r.data
    useCalendar.setState({ connection: 'connected', error: null })
    return token.accessToken
  } catch (err) {
    token = null
    const code = codeOf(err)
    const msg = messageOf(err)
    if (code === 'failed-precondition' && msg.includes('revoked')) useCalendar.setState({ connection: 'revoked' })
    else if (code === 'failed-precondition') useCalendar.setState({ connection: 'disconnected' })
    else useCalendar.setState({ error: msg })
    throw err
  }
}

/** Finds out whether this person has connected a Google account; warms the token when they have. */
export async function checkConnection(): Promise<void> {
  if (useCalendar.getState().connection === 'checking') return
  useCalendar.setState({ connection: 'checking' })
  try {
    await getToken()
    await loadCalendars()
  } catch {
    if (useCalendar.getState().connection === 'checking') useCalendar.setState({ connection: 'disconnected' })
  }
}

/** The popup flow, then the code to the function. Repeats with consent forced if Google gave no refresh token. */
export async function connectGoogle(loginHint: string, settings: Settings | undefined): Promise<void> {
  useCalendar.setState({ busy: true, error: null })
  try {
    let { code, redirectUri } = await requestAuthCode(loginHint)
    let r = await callConnect({ code, redirectUri })
    if (!r.data.connected && r.data.needsConsent) {
      ;({ code, redirectUri } = await requestAuthCode(loginHint, true))
      r = await callConnect({ code, redirectUri })
    }
    if (!r.data.connected) throw new Error('Google did not grant calendar access.')
    if (r.data.accessToken && r.data.expiresAt) token = { accessToken: r.data.accessToken, expiresAt: r.data.expiresAt }
    useCalendar.setState({ connection: 'connected' })
    await loadCalendars()
    const w = useCalendar.getState().window
    if (w) await loadEvents(w.from, w.to, settings, true)
  } catch (err) {
    if (messageOf(err) !== 'cancelled') useCalendar.setState({ error: messageOf(err) })
    throw err
  } finally {
    useCalendar.setState({ busy: false })
  }
}

export async function disconnectGoogle(): Promise<void> {
  useCalendar.setState({ busy: true, error: null })
  try {
    await callDisconnect()
  } finally {
    token = null
    for (const c of useCalendar.getState().calendars) void idbDelete(`events:${c.id}`)
    void idbDelete('calendars')
    useCalendar.setState({ busy: false, connection: 'disconnected', calendars: [], events: [], asOf: null, fromCache: false })
  }
}

export async function loadCalendars(): Promise<void> {
  const raw = await listCalendars(getToken)
  const calendars = raw.map(normalizeCalendar)
  useCalendar.setState({ calendars })
  void idbSet('calendars', calendars)
}

interface CachedEvents {
  from: DateStr
  to: DateStr
  asOf: number
  events: CalEvent[]
}

function revive(e: CalEvent): CalEvent {
  return { ...e, start: new Date(e.start), end: new Date(e.end) }
}

/** Shows what the device last fetched, with its "as of" time, until the network answers. */
export async function hydrateFromCache(settings: Settings | undefined): Promise<void> {
  const calendars = (await idbGet<GCalendar[]>('calendars')) ?? []
  if (!calendars.length) return
  const shown = calendars.filter((c) => isShown(c, settings))
  const events: CalEvent[] = []
  let asOf: number | null = null
  for (const c of shown) {
    const cached = await idbGet<CachedEvents>(`events:${c.id}`)
    if (!cached) continue
    events.push(...cached.events.map(revive))
    asOf = asOf === null ? cached.asOf : Math.min(asOf, cached.asOf)
  }
  useCalendar.setState((s) => (s.asOf === null ? { calendars: s.calendars.length ? s.calendars : calendars, events, asOf, fromCache: true } : {}))
}

let loadSeq = 0

/**
 * events.list per shown calendar for the window, in one go, cached per
 * calendar. Called when a view opens, the range changes, the window regains
 * focus, and every five minutes.
 */
export async function loadEvents(from: DateStr, to: DateStr, settings: Settings | undefined, force = false): Promise<void> {
  const state = useCalendar.getState()
  if (state.connection !== 'connected') {
    useCalendar.setState({ window: { from, to } })
    return
  }
  const same = state.window && state.window.from === from && state.window.to === to
  if (same && !force && state.asOf && Date.now() - state.asOf < 60_000) return
  const seq = (loadSeq += 1)
  useCalendar.setState({ window: { from, to }, busy: true })
  try {
    if (!state.calendars.length) await loadCalendars()
    const shown = useCalendar.getState().calendars.filter((c) => isShown(c, settings))
    const timeMin = dateTimeFrom(from, '00:00')
    const timeMax = dateTimeFrom(addDays(to, 1), '00:00')
    const results = await Promise.all(
      shown.map(async (c) => {
        const raw = await listEvents(getToken, c.id, timeMin, timeMax)
        const events = raw.map((r) => normalizeEvent(r, c)).filter((e): e is CalEvent => !!e)
        void idbSet(`events:${c.id}`, { from, to, asOf: Date.now(), events } satisfies CachedEvents)
        return events
      }),
    )
    if (seq !== loadSeq) return
    useCalendar.setState({ events: results.flat(), asOf: Date.now(), fromCache: false, error: null })
  } catch (err) {
    if (seq !== loadSeq) return
    useCalendar.setState({ error: messageOf(err) })
  } finally {
    if (seq === loadSeq) useCalendar.setState({ busy: false })
  }
}

/** For tests and sign-out: forget the token and everything loaded. */
export function resetCalendarStore(): void {
  token = null
  loadSeq += 1
  useCalendar.setState({ connection: 'unknown', busy: false, error: null, calendars: [], events: [], window: null, asOf: null, fromCache: false })
}
